import classNames from 'classnames';
import { isEmpty, omit, toNumber, trim } from 'lodash';
import { InputHTMLAttributes, memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { hasOnlyValidEnglishNameCharacters } from '@cyren/common-lib';
import { QuestionProps } from '../../pa-prop-types';
import { QsOptions } from '../../patient-types';
import { propsToIgnore } from './qs-const';
import { useStaticLocales } from '../../../admin/hooks/locales/use-static-locale';
import { useSurveyState } from '../../hooks/use-survey-state';
import { useAutocomplete } from './hooks/useAutocomplete';

function QsTextInput(
  props: InputHTMLAttributes<HTMLInputElement> &
    QuestionProps & {
      isDisplayMode?: boolean;
      options?: QsOptions | null;
      onUpdate: (value?: string) => void;
      focusOnLoad?: boolean;
      restrictInput?: 'valid name' | null;
    }
) {
  const { focusOnLoad, isDisplayMode, onUpdate, className, value, options, error, restrictInput, question } = props;
  const { max, min } = options || {};
  const filteredProps = omit(props, [...propsToIgnore, 'restrictInput']);

  // surveyState 사용
  const [{ surveyState }, { setSurveyState }] = useSurveyState();

  // 질문 타입 체크
  const isAllergyQuestion = useMemo(() => {
    return question?.questionKey === 'q-pmhx-allergy-cause-athena';
  }, [question?.questionKey]);

  const isMedicationQuestion = useMemo(() => {
    return question?.questionKey === "q-medication-pta-describe";
  }, [question?.questionKey]);

  // Pharmacy, PCP 관련 질문 여부 (추가)
  const isPharmacyQuestion = useMemo(() => {
    return question?.questionKey === "q-pharamcy-info-athena" || question?.questionKey === "q-pharamcy-info";
  }, [question?.questionKey]);
  const isPCPQuestion = useMemo(() => {
    return question?.questionKey === "q-primary-care-doctor-info-athena" || question?.questionKey === "q-primary-care-doctor-info";
  }, [question?.questionKey]);

  // 값 상태 관리
  const [initialValue, setInitialValue] = useState(value);

  // input ref with proper typing
  const inputRef = useRef<HTMLInputElement>(null);
  
  // 이전 값들을 추적하여 무한 루프 방지
  const prevAllergyRef = useRef<string | undefined>();
  const prevMedicationCurrentRef = useRef<string | undefined>();
  
  // 안정적인 함수 참조를 위한 ref
  const setSurveyStateRef = useRef(setSurveyState);
  const onUpdateRef = useRef(onUpdate);
  
  // ref 업데이트
  useEffect(() => {
    setSurveyStateRef.current = setSurveyState;
    onUpdateRef.current = onUpdate;
  });

  // 자동완성 커스텀 훅 사용
  const {
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
    autocompleteItems,
    setAutocompleteItems,
    isLoading,
    setIsLoading,
    selectedAutoValue,
    setSelectedAutoValue,
    existMedication,
    existPharmacy,
    existPCP,
    existAllergy,
    autoFillValue,
    handleSelectItem,
  } = useAutocomplete({
    isAllergyQuestion,
    isMedicationQuestion,
    isPharmacyQuestion,
    isPCPQuestion,
    questionKey: question?.questionKey,
    surveyState,
    setSurveyStateRef,
    onUpdateRef,
  });

  // 언어
  const { sltStr } = useStaticLocales();

  // 입력값 상태 및 디바운스
  const [valueTemp, setValueTemp] = useState(`${value}` || '');
  const [valueDebbed] = useDebounce(valueTemp, 300);

  // 성능 최적화: 자동완성 로직을 커스텀 훅으로 분리하고 useCallback으로 메모이제이션 적용


  // 기존 value prop이 바뀌면 valueTemp도 동기화
  useEffect(() => {
    if (initialValue !== value) {
      setInitialValue(value);
      setValueTemp(value != null ? `${value}` : '');
    }
  }, [value]);

  // TypeScript 타입 안전성: inputRef에 적절한 타입 지정

  // 포커스 온 로드
  useEffect(() => {
    if (focusOnLoad && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnLoad]);

  // 자동완성 데이터가 들어오면 자동으로 값 채우기 (최초 1회만)
  useEffect(() => {
    autoFillValue(valueTemp, setValueTemp);
  }, [autoFillValue, valueTemp]);

  // 사용자가 자동완성 값에서 벗어나 직접 수정한 경우 surveyState에 반영
  useEffect(() => {
    // Pharmacy
    if (isPharmacyQuestion && selectedAutoValue && valueDebbed !== selectedAutoValue) {
      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        pharmacyName: valueDebbed,
        pharmacyId: undefined, // id는 직접 입력시 초기화
      }));
    }
    // PCP
    if (isPCPQuestion && selectedAutoValue && valueDebbed !== selectedAutoValue) {
      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        providerName: valueDebbed,
        providerId: undefined, // id는 직접 입력시 초기화
      }));
    }
  }, [isPharmacyQuestion, isPCPQuestion, valueDebbed, selectedAutoValue]);

  // 알러지 질문일 때 surveyState에 allergy 저장
  useEffect(() => {
    if (isAllergyQuestion && valueDebbed !== prevAllergyRef.current) {
      prevAllergyRef.current = valueDebbed;
      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        allergy: valueDebbed,
      }));
    }
  }, [isAllergyQuestion, valueDebbed]);

  // medicationCurrent (이전 코드 오류 수정: isMedicationQuestion 조건)
  useEffect(() => {
    if (isMedicationQuestion && valueDebbed !== prevMedicationCurrentRef.current) {
      prevMedicationCurrentRef.current = valueDebbed;
      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        medicationCurrent: valueDebbed,
      }));
    }
  }, [isMedicationQuestion, valueDebbed]);

  // 키보드 네비게이션 핸들러 - 메모이제이션
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || autocompleteItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < autocompleteItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : autocompleteItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < autocompleteItems.length) {
          handleSelectItemWrapper(autocompleteItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showAutocomplete, autocompleteItems, selectedIndex]);

  // 아이템 선택 핸들러 - 커스텀 훅에서 가져온 함수 사용
  const handleSelectItemWrapper = useCallback((item: any) => {
    handleSelectItem(item, setValueTemp);
  }, [handleSelectItem]);

  // 유효성 검사
  let valid = true;
  let errorMessage = null;

  if (valid && min != null) {
    if (toNumber(value) < toNumber(min)) {
      valid = false;
    }
  }
  if (valid && max != null) {
    if (toNumber(value) > toNumber(max)) {
      valid = false;
    }
  }

  if (valid && !isEmpty(value) && restrictInput === 'valid name' && !hasOnlyValidEnglishNameCharacters(value)) {
    errorMessage = sltStr({ key: 'valid name' });
    valid = false;
  }

  // onChange 핸들러 - 메모이제이션
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValueTemp(e.target.value);
    // 사용자가 직접 입력하면 자동완성 선택값 해제
    if (selectedAutoValue !== null && e.target.value !== selectedAutoValue) {
      setSelectedAutoValue(null);
    }
  }, [selectedAutoValue]);

  // onBlur 핸들러 - 메모이제이션
  const handleBlur = useCallback(() => {
    // 약간의 지연을 두어 클릭 이벤트가 처리될 수 있도록 함
    setTimeout(() => {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }, 200);
  }, []);

  // onUpdate 호출
  useEffect(() => {
    if (value !== valueDebbed) {
      onUpdateRef.current(valueDebbed);
    }
  }, [valueDebbed]);

  const hasValue = !isEmpty(trim(valueDebbed));

  return (
    <div className="QsTextInput">
      {errorMessage ? <div className='text-error opacity-70 p-1'>{errorMessage}</div> : ''}
      <div className="relative">
        <input
          ref={inputRef}
          className={classNames(
            'input input-bordered w-full rounded-xl',
            className,
            hasValue && !isDisplayMode && !valid && 'input-error',
            error && 'input-error'
          )}
          {...filteredProps}
          value={valueTemp}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          min={min}
          max={max}
        />
        
        {/* 자동완성 목록 */}
        {showAutocomplete && (isPharmacyQuestion || isPCPQuestion) && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto transition-all duration-200 ease-in-out">
            {isLoading && (
              <div className="px-3 py-2 text-gray-500 text-center">
                Searching...
              </div>
            )}
            {!isLoading && autocompleteItems.map((item, index) => (
              <div
                key={index}
                className={classNames(
                  'px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150',
                  index === selectedIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-50'
                )}
                onClick={() => handleSelectItemWrapper(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {isAllergyQuestion && (
                  <div className="font-medium text-gray-900">{item.allergy}</div>
                )}
                {isMedicationQuestion && (
                  <div className="font-medium text-gray-900">{item.medication}</div>
                )}
                {isPharmacyQuestion && (
                  <div className="font-medium text-gray-900">
                    {(() => {
                      const rawName = item.name || item.pharmacyName || '';
                      // #으로 시작하는 숫자 패턴을 제거
                      return rawName.replace(/\s*#\d+/g, '');
                    })()}
                  </div>
                )}
                {isPCPQuestion && (
                  <div className="font-medium text-gray-900">{item.name || item.providerName}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {(min != null || max != null) && (
        <div className='font-bold text-sm py-1'>
          * {min != null ? `${min} ` : ''}~{max != null ? ` ${max}` : ''}
        </div>
      )}
    </div>
  );
}

export default memo(QsTextInput);
